/**
 * Category resolution.
 *
 * A vendor's content is often the same block repeated across many pages, where
 * the only thing that changes is which consultation the call-to-action opens.
 * Instead of the vendor maintaining a slug-to-questionnaire table, Evaro hosts
 * one: a static `categories.json` co-deployed with the landing manifests. The
 * vendor passes a friendly `category` slug and the SDK resolves the link.
 */

/** Maps a vendor's category slugs to Connect links. */
export type CategoryMap = Record<string, string>

/**
 * URL of a vendor's category map. It is a static file co-deployed with the
 * landing manifests, so it sits on the same CDN origin as `/v2/...`.
 */
export function categoriesUrl(cdn: string, vendor: string): string {
  return `${cdn.replace(/\/+$/, '')}/v2/${vendor}/categories.json`
}

/**
 * Resolve a category slug to a Connect link from the vendor's category map.
 *
 * Returns `null` when the map cannot be fetched or the slug is not in it. A
 * vendor that references a category Evaro has not published gets `null`, and
 * the caller renders nothing: no thrown error, no broken markup on their page.
 */
export async function resolveCategoryLink(
  cdn: string,
  vendor: string,
  category: string,
): Promise<string | null> {
  try {
    const res = await fetch(categoriesUrl(cdn, vendor), { credentials: 'omit' })
    if (!res.ok) return null
    const map = (await res.json()) as CategoryMap
    const link = map?.[category]
    return typeof link === 'string' && link.length > 0 ? link : null
  } catch {
    // Network error or malformed JSON — treated the same as "not found".
    return null
  }
}
